import React from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';

import { loadReCaptcha } from 'react-recaptcha-v3';

import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import NotificationBar from 'eventbrite_design_system/notification/NotificationBar';
import withMainControls from 'eventbrite_design_system/structure/hoc/withMainControls';

import LogoEBrandSvg from 'eventbrite_design_system/iconography/icons/LogoEBrand';

import { ICON_TYPE_PROP_TYPE } from 'eventbrite_design_system/icon/constants';
import { TYPE_ERROR } from 'eventbrite_design_system/notification/constants';
import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
} from '@eb/social-login-facebook';

import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';

import PageHeader from '../../components/PageHeader';
import LookUpForm from '../../components/forms/LookUpForm';
import AuthenticationStructure from '../../components/AuthenticationStructure';

import { BASE_URL, INVALID_RESET_TOKEN } from '../../constants';
import {
    isLoginProviderEnabled,
    normalizePath,
    checkSocialMocks,
} from '../../utils';

/**
 * List of urls that will lead to this page:
 * * BASE_URL
 */

class LookupPage extends React.PureComponent {
    static propTypes = {
        onAccountStatusCheck: PropTypes.func,
        onSocialLogInUser: PropTypes.func,
        onSocialLogInUserNotAuthorized: PropTypes.func,
        onTermsOfServiceClick: PropTypes.func,
        onComunityGuidelinesClick: PropTypes.func,
        onPrivacyPolicyClick: PropTypes.func,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        clearAuthNotification: PropTypes.func,
        fp: PropTypes.bool,
        onSocialProviderClick: PropTypes.func,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        trackSIWA_LookUpSuccessWithEmail: PropTypes.func,
        trackSIWA_LookUpSuccessWithHiddenEmail: PropTypes.func,
    };

    state = {
        invalidPasswordResetToken: false,
        shouldAnimate: false,
    };

    componentDidMount() {
        const {
            router: {
                location: { query },
            },
        } = this.props;

        /*This is needed here because the route is redirected on the server, before we have the
        chance to set state. SO once the component is loaded we can determine if this page was
        redirected to due to an invalid reset password token*/
        if (query[INVALID_RESET_TOKEN]) {
            //eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ invalidPasswordResetToken: true });
        }

        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }
    }

    // REFACTOR: All this is for an animation only
    componentWillReceiveProps({ location: { pathname } }) {
        const { route } = this.props;

        //Needed so that the submit button correctly animates up when the
        //user navigates to the /signin, but not on initial load.
        if (pathname) {
            //In certain cases the pathname is /signin/ rather than /signin
            //due to this we want to normalize the path to /signin
            const normalizedPath = normalizePath(pathname);

            //This conditional checks that the route we are going TO is the base page,
            //but the route we are currently on is a nested route (/login or /signup etc)
            if (normalizedPath === BASE_URL && has(route, 'path')) {
                this.setState({ shouldAnimate: true });
            }
        }
    }

    // Don't have a good understanding of this function yet or when is it necessary.
    forceUpdate(socialProvider, stateFromProvider) {
        const { onSocialProviderClick, onSkipSocialLinks } = this.props;

        checkSocialMocks({ callback: onSkipSocialLinks });

        if (onSocialProviderClick) {
            onSocialProviderClick(socialProvider, stateFromProvider);
        }
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onAccountStatusCheck } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onAccountStatusCheck(formData);
    };

    _handleOnSocialLogIn = (socialProvider, socialResponse) => {
        const {
            clearAuthNotification,
            onSocialLogInUser,
            socialSSOSettings,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // add param to call api, depending on feature flag
        let skipCreation = false;

        if (socialSSOSettings) {
            skipCreation = socialSSOSettings.twoStepsSignup || false;
        }

        if (onSocialLogInUser) {
            onSocialLogInUser(socialProvider, {
                skipCreation,
                ...socialResponse,
            });
        }
    };

    _handleOnSocialLogInNotAuthorized = (socialProvider, socialStatus) => {
        const { onSocialLogInUserNotAuthorized } = this.props;

        if (onSocialLogInUserNotAuthorized) {
            onSocialLogInUserNotAuthorized(socialProvider, socialStatus);
        }
    };

    _handleOnSocialProviderClick = (socialProvider, stateFromProvider) => {
        const { onSocialProviderClick, onSkipSocialLinks } = this.props;

        checkSocialMocks({ callback: onSkipSocialLinks });

        if (onSocialProviderClick) {
            onSocialProviderClick(socialProvider, stateFromProvider);
        }
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunkySvg />,
            });

            this._hasNotification = true;
        }
    };

    _renderAuthNotification = () => {
        const { authNotificationOptions } = this.props;

        let component = null;

        if (!isEmpty(authNotificationOptions)) {
            const { message, ...props } = authNotificationOptions;

            component = (
                <div className="eds-l-mar-top-4 eds-l-mar-bot-8 sign-in-screen__notification-container">
                    <NotificationBar {...props}>{message}</NotificationBar>
                </div>
            );
        }

        return component;
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    render() {
        const {
            fp,
            socialSSOSettings,
            authNotificationOptions,

            socialAuthenticationData,
            trackSIWA_LookUpSuccessWithEmail,
            trackSIWA_LookUpSuccessWithHiddenEmail,

            onGetStartedClick,
            onTermsOfServiceClick,
            onPrivacyPolicyClick,
            onComunityGuidelinesClick,
        } = this.props;

        const { invalidPasswordResetToken, shouldAnimate } = this.state;

        let trackOnPageLoad;

        if (!isEmpty(socialAuthenticationData)) {
            trackOnPageLoad = socialAuthenticationData.isEmailHidden
                ? trackSIWA_LookUpSuccessWithHiddenEmail
                : trackSIWA_LookUpSuccessWithEmail;
        }

        let pageHeaderProps = {
            headerText: gettext('Sign up or log in'),
            subtitleText: '',
            iconType: <LogoEBrandSvg />,
            iconColor: 'white',
            haloColor: 'ui-orange',
        };

        // replace subtitle if only Facebook integration is enabled
        if (
            isLoginProviderEnabled(
                socialSSOSettings,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
            ) &&
            !isLoginProviderEnabled(
                socialSSOSettings,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
            )
        ) {
            pageHeaderProps = {
                ...pageHeaderProps,
                subtitleText: gettext('Use Facebook or email to get started.'),
            };
        }

        // hide subtitle when an auth notification is being displayed
        if (!isEmpty(authNotificationOptions)) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        return (
            <AuthenticationStructure>
                <>
                    <PageHeader {...pageHeaderProps} />

                    {this._renderAuthNotification()}

                    <LookUpForm
                        buttonCTA={gettext('Get Started')}
                        // `onSubmit` will be received as handleSubmit
                        onSubmit={this._handleSubmit}
                        onSocialConnectVerified={this._handleOnSocialLogIn}
                        onSocialConnectNotAuthorized={
                            this._handleOnSocialLogInNotAuthorized
                        }
                        onSocialProviderClick={
                            this._handleOnSocialProviderClick
                        }
                        onGetStartedClick={onGetStartedClick}
                        onTermsOfServiceClick={onTermsOfServiceClick}
                        onPrivacyPolicyClick={onPrivacyPolicyClick}
                        onComunityGuidelinesClick={onComunityGuidelinesClick}
                        invalidPasswordResetToken={invalidPasswordResetToken}
                        fp={fp}
                        showNotification={this._handleShowNotification}
                        clearNotification={this._handleClearNotification}
                        shouldAnimate={shouldAnimate}
                        socialSSOSettings={socialSSOSettings}
                        socialAuthenticationData={socialAuthenticationData}
                        trackOnPageLoad={trackOnPageLoad}
                    />
                </>
            </AuthenticationStructure>
        );
    }
}

export default withMainControls(LookupPage);
